import { Form, type FormInstance } from "antd";
import type { ComponentType } from "react";
import {
  DELIVERED_ICON,
  MOVED_ICON,
  READY_TO_INSTALL_ICON,
  RECEIVED_ICON,
  RETURNED_ICON,
} from "src/assets/svgs";
import UNINSTALLED_ICON from "src/assets/icons/vehicleTypes/uninstalled.svg";
import { useQueryGetUnitTypeLookup } from "src/queries/administrationSetup.queries";
import type { NamedReference } from "src/services/administrationSetupService";
import type { WarehouseUnitNestedEntity } from "src/services/warehouseUnitService";
import type { LookupOption } from "src/services/warehouseService";

export interface UnitFormValues {
  unitTypeId?: string | number;
  makeId?: string | number;
  modelId?: string | number;
  imei?: string;
  serial?: string;
  phoneNumber?: string;
  length?: string;
  unitStatusId?: string | number;
  active?: string;
  operationTypeId?: string | number;
  warehouseId?: string | number;
  areaId?: string | number;
  laneId?: string | number;
  shelfId?: string | number;
  positionId?: string | number;
}

export const normalizeVariantKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s-_]+/g, "_");

export const getEntityCode = (entry?: NamedReference | null) =>
  String(entry?.code ?? entry?.enumName ?? "").toUpperCase();

export const getEntityName = (entry?: { name?: string; code?: string } | null) =>
  String(entry?.name ?? entry?.code ?? "");

export const isFuelSensorType = (entry?: NamedReference | null) => {
  if (!entry) return false;
  if (getEntityCode(entry) === "FUESNSR") return true;
  return normalizeVariantKey(getEntityName(entry)).includes("fuel");
};

export const OPERATION_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  received: RECEIVED_ICON as unknown as ComponentType<{ className?: string }>,
  move: MOVED_ICON as unknown as ComponentType<{ className?: string }>,
  moved: MOVED_ICON as unknown as ComponentType<{ className?: string }>,
  returned: RETURNED_ICON as unknown as ComponentType<{ className?: string }>,
  uninstalled: UNINSTALLED_ICON as unknown as ComponentType<{ className?: string }>,
  ready_to_install: READY_TO_INSTALL_ICON as unknown as ComponentType<{ className?: string }>,
  delivered: DELIVERED_ICON as unknown as ComponentType<{ className?: string }>,
};

const HIDDEN_OPERATION_TYPE_KEYS = new Set(["internal_transfer", "installed"]);

export const toAdminOptions = (items: NamedReference[]) =>
  items
    .filter((item) => item.id !== undefined)
    .map((item) => ({ value: item.id as string | number, label: getEntityName(item) }));

export const toOperationTypeOptions = (
  items: WarehouseUnitNestedEntity[],
  t: (key: string) => string
) =>
  items
    .filter((item) => item.id !== undefined)
    .filter((item) => !HIDDEN_OPERATION_TYPE_KEYS.has(normalizeVariantKey(getEntityName(item))))
    .map((item) => {
      const key = normalizeVariantKey(getEntityName(item));
      const Icon = OPERATION_ICONS[key];
      const translationKey = `operation_type_${key}`;
      const translated = t(translationKey);
      const label = translated === translationKey ? getEntityName(item) : translated;

      return {
        value: item.id as string | number,
        label: (
          <span className="flex items-center gap-2">
            {Icon ? <Icon className="h-4 w-4" /> : null}
            <span>{label}</span>
          </span>
        ),
      };
    });

export const toHwStatusOptions = (items: WarehouseUnitNestedEntity[], t: (key: string) => string) =>
  items
    .filter((item) => item.id !== undefined)
    .map((item) => {
      const key = normalizeVariantKey(getEntityName(item));
      const translationKey = `hw_status_${key}`;
      const translated = t(translationKey);
      const label = translated === translationKey ? getEntityName(item) : translated;
      return { value: item.id as string | number, label };
    });

export const mapLookupOptions = (options: LookupOption[]) =>
  options.map((option) => ({
    value: option.id,
    label: option.name ?? option.code ?? String(option.id),
  }));

export const getLookupOptions = (data: { response?: LookupOption[] } | LookupOption[] | undefined) =>
  Array.isArray(data) ? data : (data?.response ?? []);

export const getOptionalDistributorId = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export const useUnitTypeLookup = (open: boolean) => {
  const { data, isLoading } = useQueryGetUnitTypeLookup(open);
  const items = data?.data?.response ?? [];
  const options = toAdminOptions(items);
  return { items, options, isLoading };
};

export const findEntryById = (
  items: NamedReference[],
  id: string | number | undefined
) => items.find((item) => id !== undefined && String(item.id) === String(id));

export const useIsFuelSensor = (
  form: FormInstance<UnitFormValues>,
  open: boolean
) => {
  const selectedUnitTypeId = Form.useWatch("unitTypeId", form);
  const { items } = useUnitTypeLookup(open);
  const entry = findEntryById(items, selectedUnitTypeId);
  return isFuelSensorType(entry);
};
